import { css } from '@emotion/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import JsonViewer from '../WorkflowRunDetailsPage/JsonViewer/JsonViewer';

import useWorkflowErrorSummaryAsync from 'store/useWorkflowErrorSummaryAsync';

import IRunEntry from 'types/IRunEntry';

export default function ErrorSummaryDialog({ run, onClose }: { run: IRunEntry; onClose: () => void }) {
  const errorSummaryAsync = useWorkflowErrorSummaryAsync(run.project_id, run.id);
  const summary =
    errorSummaryAsync.status === 'SUCCESS' || errorSummaryAsync.status === 'REFRESH'
      ? errorSummaryAsync.data
      : undefined;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Error summary — {run.name}</DialogTitle>
      <DialogContent>
        {['INIT', 'PENDING'].includes(errorSummaryAsync.status) && <LinearProgress />}
        {errorSummaryAsync.status === 'ERROR' && <Alert severity="error">Could not load the error summary</Alert>}
        {summary && (
          <>
            <Typography gutterBottom>
              {summary.total_failed_tasks} failed {summary.total_failed_tasks === 1 ? 'task' : 'tasks'}
            </Typography>
            {summary.errors.map((error) => (
              <Accordion key={error.error_type}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <span
                    css={css`
                      font-weight: 600;
                    `}
                  >
                    {error.error_type}
                  </span>
                  <Typography component="span" color="text.secondary" sx={{ ml: 1 }}>
                    — {error.count} {error.count === 1 ? 'task' : 'tasks'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {error.sample_message && <Typography gutterBottom>{error.sample_message}</Typography>}
                  {error.sample_traceback && (
                    <Box
                      component="pre"
                      sx={{
                        maxHeight: 300,
                        overflow: 'auto',
                        p: 1.5,
                        bgcolor: 'action.hover',
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                      }}
                    >
                      {error.sample_traceback}
                    </Box>
                  )}
                  {!!error.sample_task_ids?.length && (
                    <Typography variant="caption" color="text.secondary">
                      Sample task ids: {error.sample_task_ids.join(', ')}
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
            <details
              css={css`
                margin-top: 16px;
              `}
            >
              <summary>Raw JSON</summary>
              <JsonViewer json={summary} />
            </details>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button component={RouterLink} to={`/projects/${run.project_id}/workflows/${run.id}/details`}>
          Open details
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
